"use client";

import { useEffect, useState } from "react";
import { Menu } from "lucide-react";
import { Link } from "@/components/common/Link";
import { MenuProvider, useMenu } from "@/integrations/wordpress/WordPressMenuProvider";
import { WvcLogo } from "@/integrations/wordpress/WvcLogo";
import { Button } from "@/components/ui/button";
import { NavigationMenu, NavigationMenuItem, NavigationMenuLink, NavigationMenuList } from "@/components/ui/navigation-menu";
import { Sheet, SheetClose, SheetContent, SheetHeader, SheetTitle, SheetTrigger } from "@/components/ui/sheet";
function DesktopMenuConsumer() {
  const {
    menuItems,
    loading
  } = useMenu();
  if (loading) {
    return <div className="flex gap-2">
        <div className="h-6 w-20 bg-muted animate-pulse rounded-md" />
        <div className="h-6 w-20 bg-muted animate-pulse rounded-md" />
        <div className="h-6 w-20 bg-muted animate-pulse rounded-md" />
        <div className="h-6 w-20 bg-muted animate-pulse rounded-md" />
      </div>;
  }
  return <NavigationMenu viewport={false}>
      <NavigationMenuList className="gap-1">
        {menuItems.map((item, i) => <NavigationMenuItem key={item.id} data-index={i}>
            <NavigationMenuLink asChild>
              <Link to={item.href || "#"} className="group relative px-3 py-2 text-sm font-default tracking-widest text-foreground bg-transparent hover:bg-transparent focus:bg-transparent">
                <span className="relative">
                  {item.label}
                  <span className="pointer-events-none absolute -bottom-1 left-0 h-px w-0 bg-secondary transition-all duration-300 group-hover:w-full" />
                </span>
              </Link>
            </NavigationMenuLink>
          </NavigationMenuItem>)}
      </NavigationMenuList>
    </NavigationMenu>;
}
function MobileMenuConsumer() {
  const {
    menuItems,
    loading
  } = useMenu();
  if (loading) {
    return <div className="flex flex-col gap-4 mt-4">
        <div className="h-7 w-40 bg-muted animate-pulse rounded-md" />
        <div className="h-7 w-40 bg-muted animate-pulse rounded-md" />
        <div className="h-7 w-40 bg-muted animate-pulse rounded-md" />
      </div>;
  }
  return <nav className="flex flex-col gap-2 px-2">
      {menuItems.map((item, i) => <SheetClose asChild key={item.id} data-index={i}>
          <Link to={item.href || "#"} className="block py-3 text-lg font-default tracking-wide text-foreground border-b border-border/60 transition-colors hover:text-secondary">
            {item.label}
          </Link>
        </SheetClose>)}
    </nav>;
}
export default function Header() {
  const [scrolled, setScrolled] = useState(false);
  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    onScroll();
    window.addEventListener("scroll", onScroll, {
      passive: true
    });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);
  useEffect(() => {
    const handleAnchorClick = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      const anchor = target.closest("a[href]") as HTMLAnchorElement | null;
      if (anchor) {
        const href = anchor.getAttribute("href");
        if (!href) return;
        if (href === "/#") {
          e.preventDefault();
          window.scrollTo({
            top: 0,
            behavior: "smooth"
          });
          return;
        }
        let hash = "";
        if (href.startsWith("#")) {
          hash = href;
        } else if (href.startsWith("/") && href.includes("#")) {
          const [path, hashPart] = href.split("#");
          if (hashPart === "") {
            e.preventDefault();
            window.scrollTo({
              top: 0,
              behavior: "smooth"
            });
            return;
          }
          if (path === "/" || path === window.location.pathname) {
            hash = "#" + hashPart;
          }
        }
        if (hash && hash !== "#") {
          const element = document.querySelector(hash);
          if (element) {
            e.preventDefault();
            element.scrollIntoView({
              behavior: "smooth",
              block: "start"
            });
          }
        }
      }
    };
    document.addEventListener("click", handleAnchorClick);
    return () => document.removeEventListener("click", handleAnchorClick);
  }, []);
  return <section data-section-id="1968" id="header" className={`sticky top-0 z-50 w-full transition-all duration-300 bg-background text-foreground ${scrolled ? "bg-background/80 backdrop-blur-lg border-b border-border" : "bg-background border-b border-transparent"}`}>
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between py-5">
          {}
          <Link to="/" className="flex items-center shrink-0">
            <WvcLogo className="h-7 w-auto" />
          </Link>

          {}
          <div className="hidden lg:flex flex-1 items-center justify-center">
            <MenuProvider menu_id="2" className="header-menu">
              <DesktopMenuConsumer />
            </MenuProvider>
          </div>

          {}
          <div className="hidden lg:flex items-center shrink-0">
            <Button asChild className="bg-primary text-primary-foreground tracking-wide px-8 hover:bg-primary/90 shadow-md">
              <Link to="/JoinUsPage">Download Planting Guide</Link>
            </Button>
          </div>

          {}
          <div className="flex lg:hidden items-center">
            <Sheet>
              <SheetTrigger asChild>
                <Button variant="ghost" size="icon" className="text-foreground" aria-label="Open menu">
                  <Menu className="size-6" />
                </Button>
              </SheetTrigger>
              <SheetContent side="right" className="bg-background flex flex-col">
                <SheetHeader>
                  <SheetTitle className="font-default text-xl tracking-wide text-foreground">
                    Navigation
                  </SheetTitle>
                </SheetHeader>

                <div className="flex-1 overflow-y-auto">
                  <MenuProvider menu_id="2" className="mobile-menu">
                    <MobileMenuConsumer />
                  </MenuProvider>
                </div>

                <div className="p-4">
                  <SheetClose asChild>
                    <Button asChild className="w-full bg-primary text-primary-foreground tracking-wide hover:bg-primary/90">
                      <Link to="/JoinUsPage">Download Planting Guide</Link>
                    </Button>
                  </SheetClose>
                </div>
              </SheetContent>
            </Sheet>
          </div>
        </div>
      </div>
    </section>;
}